import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { TcpPortPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'
import { breadcrumbList, jsonLdScript, webPage } from '@/lib/schema'

export const Route = createFileRoute('/_site/features/tcp-monitoring')({
  component: TcpMonitoringPage,
  head: () => ({
    meta: [
      { title: 'TCP Port Monitoring — Service Checks | Uptrack' },
      { name: 'description', content: 'Monitor any TCP port: databases, mail servers, game servers. Connection-time tracking, instant port-failure alerts.' },
      { property: 'og:title', content: 'TCP Port Monitoring — Service Checks | Uptrack' },
      { property: 'og:description', content: 'Monitor any TCP port. Check databases, mail servers, custom services. Instant failure alerts.' },
      { property: 'og:image', content: 'https://uptrack.app/og/features-tcp-monitoring.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/features/tcp-monitoring' },
    ],
    scripts: [
      jsonLdScript(breadcrumbList([
        { name: 'Home', url: 'https://uptrack.app' },
        { name: 'Features', url: 'https://uptrack.app/features' },
        { name: 'TCP Monitoring', url: 'https://uptrack.app/features/tcp-monitoring' },
      ])),
      jsonLdScript(webPage({
        name: 'Uptrack TCP Port Monitoring',
        description: 'TCP port monitoring for databases, services, and custom applications with connection time tracking.',
        url: 'https://uptrack.app/features/tcp-monitoring',
      })),
    ],
  }),
})

function TcpMonitoringPage() {
  return (
    <FeaturePage
      section="TCP port monitoring"
      title="TCP port monitoring for any service."
      intro="Check any TCP port on any host. Monitor databases, mail servers, game servers, and custom services. Measure connection time and get alerted on failures."
      featuresHeading="Monitor more than just websites"
      features={[
        { title: 'Any TCP port', desc: 'PostgreSQL on 5432, Redis on 6379, SMTP on 25, SSH on 22 — monitor any port on any host.' },
        { title: 'Connection time tracking', desc: 'Measure TCP handshake latency over time. Spot network degradation before it causes timeouts.' },
        { title: 'Database monitoring', desc: 'Verify your database is accepting connections without needing credentials or query-level access.' },
        { title: 'Service health checks', desc: 'Confirm critical infrastructure services are reachable. If the port is closed, you know instantly.' },
      ]}
      table={{
        heading: 'Uptrack vs the alternatives',
        intro: 'TCP monitoring should be a baseline feature, not an upsell.',
        columns: ['Provider', 'TCP Monitor', 'Connection Time', 'Included'],
        rows: [
          { cells: ['UptimeRobot', 'Yes', 'No', 'Yes'] },
          { cells: ['Better Stack', 'Yes', 'Yes', 'Limited'] },
          { cells: ['Pingdom', 'No', '—', 'No free plan'] },
          { cells: ['Uptrack', 'Yes', 'Yes', 'Paid plans'], highlight: true },
        ],
      }}
      why={{ heading: 'How TCP monitoring works', body: 'Uptrack opens a TCP connection to your host and port at every check interval. A successful TCP handshake means the service is accepting connections. A timeout or refused connection triggers an alert. No agents, no credentials, no firewall changes needed — just a reachable port.' }}
      cta={{ heading: 'Monitor your services now', body: 'Every paid plan includes 30-second checks and alert confirmation.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard/monitors">
          <TcpPortPiece />
        </ProductFrame>
      }
    />
  )
}
